// Music Module Component - FASE 6 Mejorado
import React, { useState, useEffect, useRef } from 'react';
import SectionAgent from './SectionAgent';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Music, Plus, Mic, MicOff, Play, Pause, ListMusic,
  ExternalLink, Search, Heart, Share2, Star, Youtube,
  Headphones, Radio, Disc, User, Image as ImageIcon, X, Volume2
} from 'lucide-react';
import ShareModal from './ShareModal';
import Lightbox from './Lightbox';
import AIAgentModule from './AIAgentModule';
import MusicConnect from './MusicConnect';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MusicModule = ({ user }) => {
  const [activeTab, setActiveTab] = useState('search');
  const [playlists, setPlaylists] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [identifiedSong, setIdentifiedSong] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [listening, setListening] = useState(false);
  const [identifyListening, setIdentifyListening] = useState(false);
  const [identifyCountdown, setIdentifyCountdown] = useState(0);
  const [instructions, setInstructions] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [shareModal, setShareModal] = useState({ open: false, song: null });
  const [lightbox, setLightbox] = useState({ open: false, content: null, type: null });
  const [newPlaylist, setNewPlaylist] = useState({ name: '', description: '' });
  const [showCreatePlaylist, setShowCreatePlaylist] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  
  // Audio preview state
  const [playingPreview, setPlayingPreview] = useState(null);
  const audioRef = useRef(null);
  const identifyTimerRef = useRef(null);

  useEffect(() => {
    loadData();
    // Initialize audio element
    audioRef.current = new Audio();
    audioRef.current.volume = 0.5;
    audioRef.current.addEventListener('ended', () => setPlayingPreview(null));
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (identifyTimerRef.current) {
        clearInterval(identifyTimerRef.current);
      }
    };
  }, [user.id]);

  const loadData = async () => {
    try {
      const [playlistRes, instructionsRes, genresRes, favRes] = await Promise.all([
        axios.get(`${API}/music/playlists/${user.id}`),
        axios.get(`${API}/music/integration-instructions`),
        axios.get(`${API}/music/genres`),
        axios.get(`${API}/music/favorites/${user.id}`)
      ]);
      setPlaylists(playlistRes.data);
      setInstructions(instructionsRes.data);
      setGenres(genresRes.data.genres || []);
      setFavorites(favRes.data || []);
    } catch (e) {}
  };

  const searchMusic = async (query = searchQuery, genre = selectedGenre) => {
    if (!query && !genre) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.append('query', query);
      if (genre) params.append('genre', genre);
      const res = await axios.get(`${API}/music/search?${params.toString()}`);
      
      // Enrich results with album art images using deterministic seeds
      const enrichedResults = (res.data.results || []).map((song, idx) => {
        const artistSeed = song.artist?.replace(/\s+/g, '-').toLowerCase() || 'artist';
        const albumSeed = song.album?.replace(/\s+/g, '-').toLowerCase() || 'album';
        const titleSeed = song.title?.replace(/\s+/g, '-').toLowerCase() || 'song';
        
        return {
          ...song,
          id: song.id || `${artistSeed}-${titleSeed}-${idx}`,
          // Generate consistent album art based on artist/album name
          album_art: song.album_art || `https://picsum.photos/seed/${encodeURIComponent(`${artistSeed}-${albumSeed}`)}/300/300`,
          // Add artist image
          artist_image: song.artist_image || `https://picsum.photos/seed/${encodeURIComponent(`artist-${artistSeed}`)}/300/300`,
          // Ensure all fields are present
          duration: song.duration || '3:45',
          year: song.year || new Date().getFullYear(),
          album: song.album || 'Álbum desconocido',
          // Generate YouTube search URL if not present
          youtube_url: song.youtube_url || `https://www.youtube.com/results?search_query=${encodeURIComponent(`${song.artist} ${song.title}`)}`,
          // Generate Spotify search URL if not present
          spotify_url: song.spotify_url || `https://open.spotify.com/search/${encodeURIComponent(`${song.artist} ${song.title}`)}`
        };
      });
      
      setSearchResults(enrichedResults);
    } catch (e) {
      toast.error('Error en la búsqueda');
    } finally {
      setLoading(false);
    }
  };

  // Identificar canción con micrófono REAL (captura audio y envía a Shazam)
  const identifySongWithMic = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Tu navegador no soporta grabación de audio');
      return;
    }
    
    setIdentifyListening(true);
    setIdentifyCountdown(10);
    setIdentifiedSong(null);
    
    toast.info('Escuchando por 10 segundos... Acerca el micrófono a la música', { duration: 10000 });
    
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Setup MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      const audioChunks = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };
      
      // Countdown timer
      identifyTimerRef.current = setInterval(() => {
        setIdentifyCountdown(prev => {
          if (prev <= 1) {
            clearInterval(identifyTimerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Start recording
      mediaRecorder.start(1000); // Collect data every second
      
      // Stop after 10 seconds and process
      setTimeout(async () => {
        mediaRecorder.stop();
        stream.getTracks().forEach(track => track.stop());
        setIdentifyListening(false);
        setIdentifyCountdown(0);
        
        // Wait for last data chunk
        await new Promise(resolve => {
          mediaRecorder.onstop = resolve;
        });
        
        // Create blob and convert to base64
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
        
        // Convert to base64
        const reader = new FileReader();
        reader.readAsDataURL(audioBlob);
        reader.onloadend = async () => {
          try {
            const base64Audio = reader.result.split(',')[1]; // Remove data:audio/webm;base64, prefix
            
            toast.loading('Identificando canción con Shazam...', { id: 'shazam-identify' });
            
            // Send to backend for Shazam identification
            const res = await axios.post(`${API}/music/identify`, {
              audio_snippet: base64Audio
            });
            
            toast.dismiss('shazam-identify');
            
            if (res.data.identified) {
              setIdentifiedSong(res.data);
              toast.success(`¡Identificada: ${res.data.song?.title} - ${res.data.song?.artist}!`);
            } else {
              toast.error(res.data.message || 'No se pudo identificar la canción. Intenta con mejor calidad de audio.');
            }
          } catch (e) {
            toast.dismiss('shazam-identify');
            toast.error('Error al identificar. Intenta de nuevo.');
            console.error('Shazam error:', e);
          }
        };
        
      }, 10000);
      
    } catch (err) {
      setIdentifyListening(false);
      setIdentifyCountdown(0);
      if (err.name === 'NotAllowedError') {
        toast.error('Permiso de micrófono denegado. Habilita el acceso al micrófono.');
      } else {
        toast.error('Error al acceder al micrófono');
      }
      console.error('Mic error:', err);
    }
  };

  const cancelIdentify = () => {
    setIdentifyListening(false);
    setIdentifyCountdown(0);
    if (identifyTimerRef.current) {
      clearInterval(identifyTimerRef.current);
    }
    toast.info('Identificación cancelada');
  };

  const createPlaylist = async () => {
    if (!newPlaylist.name) return;
    try {
      await axios.post(`${API}/music/playlists?user_id=${user.id}`, { ...newPlaylist, songs: [] });
      toast.success('Playlist creada');
      setNewPlaylist({ name: '', description: '' });
      setShowCreatePlaylist(false);
      loadData();
    } catch (e) {
      toast.error('Error');
    }
  };

  const [addToPlaylistModal, setAddToPlaylistModal] = useState({ open: false, song: null });
  const [quickPlName, setQuickPlName] = useState('');

  const addToPlaylist = async (playlistId, song) => {
    try {
      const { id: _ignored, ...songNoId } = song || {};
      await axios.post(`${API}/music/playlists/${playlistId}/songs?user_id=${user.id}`,
        { ...songNoId, title: song.title, artist: song.artist });
      toast.success('Agregada a la playlist');
      setAddToPlaylistModal({ open: false, song: null });
      loadData();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Error desconocido';
      toast.error(`No se pudo agregar a la playlist: ${detail}`);
      console.error('addToPlaylist error:', e);
    }
  };

  const addToFavorites = async (song) => {
    try {
      const { id: _ignored, ...songNoId } = song || {};
      await axios.post(`${API}/music/favorites?user_id=${user.id}`,
        { ...songNoId, title: song.title, artist: song.artist });
      toast.success('Agregada a favoritos');
      loadData();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Error desconocido';
      toast.error(`No se pudo agregar a favoritos: ${detail}`);
      console.error('addToFavorites error:', e);
    }
  };

  const handleVoice = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Tu navegador no soporta reconocimiento de voz');
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'es-MX';
    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onresult = (e) => {
      const text = e.results[0][0].transcript;
      setSearchQuery(text);
      searchMusic(text);
    };
    listening ? recognition.stop() : recognition.start();
  };

  const handleGenreClick = (genre) => {
    setSelectedGenre(genre === selectedGenre ? null : genre);
    searchMusic(searchQuery, genre === selectedGenre ? null : genre);
  };

  const playPreview = (song) => {
    if (playingPreview === song.id) {
      audioRef.current?.pause();
      setPlayingPreview(null);
    } else {
      // Use sample audio for preview
      const audioSrc = `https://www.soundhelix.com/examples/mp3/SoundHelix-Song-${(parseInt(song.id?.toString().slice(-1) || '1') % 16) + 1}.mp3`;
      if (audioRef.current) {
        audioRef.current.src = audioSrc;
        audioRef.current.play().catch(() => {
          toast.error('No se pudo reproducir el preview');
        });
        setPlayingPreview(song.id);
      }
    }
  };

  // Generate artist image
  const getArtistImage = (artistName, songTitle, idx) => {
    const seed = `${artistName}-${songTitle}-${idx}`.replace(/\s+/g, '-');
    return `https://picsum.photos/seed/${encodeURIComponent(seed)}/200/200`;
  };

  const SongCard = ({ song, showActions = true, idx = 0 }) => (
    <div className="glass-panel p-3 md:p-4 flex flex-col sm:flex-row sm:items-center gap-3 md:gap-4 group hover:ring-2 hover:ring-red-500/30 transition-all">
      {/* Album Art */}
      <div 
        className="w-14 h-14 md:w-16 md:h-16 rounded-lg overflow-hidden cursor-pointer flex-shrink-0 bg-white/10 relative"
        onClick={() => setLightbox({ open: true, content: song.album_art || getArtistImage(song.artist, song.title, idx), type: 'image' })}
      >
        <img 
          src={song.album_art || getArtistImage(song.artist, song.title, idx)} 
          alt={song.title} 
          className="w-full h-full object-cover"
          onError={(e) => { 
            e.target.src = getArtistImage(song.artist, song.title, idx);
          }}
        />
        {/* Play Preview Button on hover */}
        <button 
          onClick={(e) => { e.stopPropagation(); playPreview(song); }}
          className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          {playingPreview === song.id ? <Pause className="text-white" size={24} /> : <Play className="text-white" size={24} />}
        </button>
      </div>

      {/* Song Info */}
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium truncate">{song.title}</p>
        <p className="text-gray-500 text-sm truncate">{song.artist}</p>
        <p className="text-gray-600 text-xs truncate">{song.album} • {song.year}</p>
      </div>

      {/* Duration */}
      <span className="text-gray-500 text-sm hidden md:block">{song.duration}</span>

      {/* Actions */}
      {showActions && (
        <div className="flex items-center gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          {/* Play Preview */}
          <button 
            onClick={() => playPreview(song)}
            className={`p-2 rounded-lg transition-colors ${playingPreview === song.id ? 'text-green-400 bg-green-500/20' : 'text-gray-400 hover:text-green-400 hover:bg-green-500/20'}`}
            title="Escuchar preview"
          >
            {playingPreview === song.id ? <Pause size={18} /> : <Headphones size={18} />}
          </button>

          {/* Play on YouTube — embed modal */}
          {song.youtube_url && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const ytQuery = encodeURIComponent(`${song.artist || ''} ${song.title || ''}`.trim());
                // Buscamos el primer video y embebemos via Lightbox tipo youtube
                setLightbox({
                  open: true,
                  type: 'youtube',
                  content: `https://www.youtube.com/embed/?listType=search&list=${ytQuery}`
                });
              }}
              className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
              title="Ver video de YouTube"
            >
              <Youtube size={18} />
            </button>
          )}
          
          {/* Play on Spotify */}
          {song.spotify_url && (
            <a 
              href={song.spotify_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-2 text-green-500 hover:bg-green-500/20 rounded-lg transition-colors"
              title="Escuchar en Spotify"
            >
              <Music size={18} />
            </a>
          )}

          {/* Add to Playlist — abre modal global */}
          <button
            onClick={(e) => { e.stopPropagation(); setAddToPlaylistModal({ open: true, song }); }}
            className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors"
            title="Agregar a playlist"
          >
            <Plus size={18} />
          </button>

          {/* Add to Favorites */}
          <button 
            onClick={() => addToFavorites(song)}
            className="p-2 text-pink-400 hover:bg-pink-500/20 rounded-lg transition-colors"
            title="Agregar a favoritos"
          >
            <Heart size={18} />
          </button>

          {/* Share */}
          <button 
            onClick={() => setShareModal({ open: true, song })}
            className="p-2 text-green-400 hover:bg-green-500/20 rounded-lg transition-colors"
            title="Compartir"
          >
            <Share2 size={18} />
          </button>
        </div>
      )}
    </div>
  );

  return (
      <>
      <SectionAgent section="music" />
      <div className="p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-white" data-testid="music-title">Música</h1>
        <button 
          onClick={handleVoice} 
          className={`p-3 rounded-xl transition-colors ${listening ? 'bg-red-500' : 'bg-white/10 hover:bg-white/20'}`}
          data-testid="voice-search-btn"
        >
          {listening ? <MicOff className="text-white" /> : <Mic className="text-white" />}
        </button>
      </div>

      {/* Tabs - responsive */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button onClick={() => setActiveTab('search')} className={`tab-button ${activeTab === 'search' ? 'active' : 'inactive'}`}>
          <Search size={14} />Buscar
        </button>
        <button onClick={() => setActiveTab('identify')} className={`tab-button ${activeTab === 'identify' ? 'active' : 'inactive'}`}>
          <Radio size={14} />Identificar
        </button>
        <button onClick={() => setActiveTab('playlists')} className={`tab-button ${activeTab === 'playlists' ? 'active' : 'inactive'}`}>
          <ListMusic size={14} />Playlists
        </button>
        <button onClick={() => setActiveTab('favorites')} className={`tab-button ${activeTab === 'favorites' ? 'active' : 'inactive'}`}>
          <Heart size={14} />Favoritos
        </button>
        <button onClick={() => setActiveTab('connect')} className={`tab-button ${activeTab === 'connect' ? 'active' : 'inactive'}`}>
          <Headphones size={14} />Conectar
        </button>
      </div>

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div className="space-y-6">
          {/* Search Bar */}
          <div className="glass-panel p-4 md:p-6">
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchMusic()}
                  placeholder="Buscar canción, artista o álbum..."
                  className="input-glass pl-12 w-full"
                  data-testid="music-search-input"
                />
              </div>
              <button onClick={() => searchMusic()} disabled={loading} className="btn-primary px-6 md:px-8">
                {loading ? 'Buscando...' : 'Buscar'}
              </button>
            </div>

            {/* Genre Filters - scrollable on mobile */}
            <div className="flex flex-wrap gap-2 overflow-x-auto pb-2">
              {genres.map(genre => (
                <button
                  key={genre}
                  onClick={() => handleGenreClick(genre)}
                  className={`px-3 md:px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    selectedGenre === genre
                      ? 'bg-red-500 text-white'
                      : 'bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white'
                  }`}
                >
                  {genre}
                </button>
              ))}
            </div>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-white font-semibold">{searchResults.length} resultados</h3>
              {searchResults.map((song, idx) => (
                <SongCard key={idx} song={song} idx={idx} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Identify Tab */}
      {activeTab === 'identify' && (
        <div className="glass-panel p-6 md:p-8 text-center">
          {/* Identify Button with countdown */}
          <div className="relative">
            <button
              onClick={identifyListening ? cancelIdentify : identifySongWithMic}
              disabled={false}
              className={`w-28 h-28 md:w-32 md:h-32 rounded-full flex items-center justify-center mx-auto mb-6 transition-all shadow-lg ${
                identifyListening 
                  ? 'bg-gradient-to-br from-red-600 to-orange-600 animate-pulse shadow-red-500/50' 
                  : 'bg-gradient-to-br from-red-500 to-orange-500 hover:scale-105 shadow-red-500/30'
              }`}
              data-testid="identify-btn"
            >
              {identifyListening ? (
                <div className="text-center">
                  <span className="text-white text-3xl font-bold">{identifyCountdown}</span>
                  <p className="text-white/80 text-xs">segundos</p>
                </div>
              ) : (
                <Radio className="text-white" size={48} />
              )}
            </button>
          </div>
          
          <h3 className="text-xl font-semibold text-white mb-2">
            {identifyListening ? 'Escuchando...' : 'Identificar Canción'}
          </h3>
          <p className="text-gray-500 mb-4">
            {identifyListening 
              ? 'Acerca el micrófono a la fuente de música' 
              : 'Toca el botón y reproduce la canción cerca del micrófono (15 segundos)'
            }
          </p>
          
          {identifyListening && (
            <button 
              onClick={cancelIdentify}
              className="btn-secondary"
            >
              Cancelar
            </button>
          )}

          {/* Identified Song Result */}
          {identifiedSong && !identifyListening && (
            <div className="mt-8 glass-panel p-4 md:p-6 text-left max-w-md mx-auto animate-fade-in">
              <div className="flex items-center gap-4 mb-4">
                <div 
                  className="w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden cursor-pointer flex-shrink-0"
                  onClick={() => setLightbox({ open: true, content: identifiedSong.album_art || getArtistImage(identifiedSong.song?.artist, identifiedSong.song?.title, 0), type: 'image' })}
                >
                  <img 
                    src={identifiedSong.album_art || getArtistImage(identifiedSong.song?.artist, identifiedSong.song?.title, 0)} 
                    alt="" 
                    className="w-full h-full object-cover"
                    onError={(e) => { 
                      e.target.src = getArtistImage(identifiedSong.song?.artist, identifiedSong.song?.title, 0);
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-lg md:text-xl font-bold text-white truncate">{identifiedSong.song?.title}</h4>
                  <p className="text-gray-400 truncate">{identifiedSong.song?.artist}</p>
                  <p className="text-gray-500 text-sm truncate">{identifiedSong.song?.album} • {identifiedSong.song?.year}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {identifiedSong.youtube_url && (
                  <a href={identifiedSong.youtube_url} target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm flex items-center gap-2">
                    <Youtube size={16} />YouTube
                  </a>
                )}
                {identifiedSong.spotify_url && (
                  <a href={identifiedSong.spotify_url} target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm flex items-center gap-2">
                    <Music size={16} />Spotify
                  </a>
                )}
                <button onClick={() => addToFavorites(identifiedSong.song)} className="btn-secondary text-sm flex items-center gap-2">
                  <Heart size={16} />Favoritos
                </button>
                <button onClick={() => setShareModal({ open: true, song: identifiedSong.song })} className="btn-secondary text-sm flex items-center gap-2">
                  <Share2 size={16} />Compartir
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Playlists Tab - responsive grid */}
      {activeTab === 'playlists' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Playlists List */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-semibold">Mis Playlists</h3>
              <button onClick={() => setShowCreatePlaylist(true)} className="p-2 bg-red-500 rounded-lg">
                <Plus className="text-white" size={18} />
              </button>
            </div>

            {showCreatePlaylist && (
              <div className="glass-panel p-4 space-y-3 animate-fade-in">
                <input
                  type="text"
                  value={newPlaylist.name}
                  onChange={(e) => setNewPlaylist({ ...newPlaylist, name: e.target.value })}
                  placeholder="Nombre de la playlist"
                  className="input-glass w-full"
                />
                <div className="flex gap-2">
                  <button onClick={() => setShowCreatePlaylist(false)} className="btn-secondary flex-1">Cancelar</button>
                  <button onClick={createPlaylist} className="btn-primary flex-1">Crear</button>
                </div>
              </div>
            )}

            {playlists.map(playlist => (
              <button
                key={playlist.id}
                onClick={() => setSelectedPlaylist(playlist)}
                className={`w-full glass-panel p-4 text-left transition-all ${
                  selectedPlaylist?.id === playlist.id ? 'ring-2 ring-red-500' : 'hover:bg-white/5'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ListMusic className="text-white" size={20} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">{playlist.name}</p>
                    <p className="text-gray-500 text-sm">{playlist.songs?.length || 0} canciones</p>
                  </div>
                </div>
              </button>
            ))}

            {playlists.length === 0 && !showCreatePlaylist && (
              <div className="text-center py-8">
                <ListMusic className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">Sin playlists</p>
              </div>
            )}
          </div>

          {/* Selected Playlist Content */}
          <div className="lg:col-span-2">
            {selectedPlaylist ? (
              <div className="glass-panel p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                  <div>
                    <h3 className="text-lg md:text-xl font-semibold text-white">{selectedPlaylist.name}</h3>
                    <p className="text-gray-500">{selectedPlaylist.songs?.length || 0} canciones</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShareModal({ open: true, song: { ...selectedPlaylist, title: selectedPlaylist.name } })}
                      className="btn-secondary flex items-center gap-2"
                    >
                      <Share2 size={16} />
                      <span className="hidden sm:inline">Compartir</span>
                    </button>
                    <button onClick={() => setSelectedPlaylist(null)} className="lg:hidden btn-secondary">
                      <X size={16} />
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  {(selectedPlaylist.songs || []).map((song, idx) => (
                    <SongCard key={idx} song={song} idx={idx} />
                  ))}
                  {(!selectedPlaylist.songs || selectedPlaylist.songs.length === 0) && (
                    <p className="text-gray-500 text-center py-8">Playlist vacía</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="glass-panel p-8 md:p-12 text-center">
                <ListMusic className="w-12 h-12 md:w-16 md:h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500">Selecciona una playlist</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Favorites Tab */}
      {activeTab === 'favorites' && (
        <div className="space-y-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Heart className="text-pink-400" size={20} />
            Mis Favoritos ({favorites.length})
          </h3>
          {favorites.length === 0 ? (
            <div className="glass-panel p-8 md:p-12 text-center">
              <Heart className="w-12 h-12 md:w-16 md:h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500">Sin canciones favoritas</p>
            </div>
          ) : (
            <div className="space-y-3">
              {favorites.map((song, idx) => (
                <SongCard key={idx} song={song} idx={idx} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Connect Tab - servicios musicales funcionales */}
      {activeTab === 'connect' && (
        <MusicConnect user={user} />
      )}

      {/* Share Modal */}
      <ShareModal
        isOpen={shareModal.open}
        onClose={() => setShareModal({ open: false, song: null })}
        itemType="song"
        itemId={shareModal.song?.id}
        itemTitle={shareModal.song?.title || 'Canción'}
        itemData={{ userId: user.id, ...shareModal.song }}
      />

      {/* Lightbox */}
      <Lightbox
        isOpen={lightbox.open}
        onClose={() => setLightbox({ open: false, content: null, type: null })}
        content={lightbox.content}
        type={lightbox.type}
      />

      {/* Add to Playlist Modal */}
      {addToPlaylistModal.open && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4" style={{ zIndex: 99999 }}
             onClick={() => setAddToPlaylistModal({ open: false, song: null })}>
          <div className="bg-[#0d1117] border border-white/10 rounded-2xl max-w-md w-full p-6 shadow-2xl"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3 min-w-0">
                {addToPlaylistModal.song?.album_art && (
                  <img src={addToPlaylistModal.song.album_art} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-xs text-gray-400">Agregar a playlist</p>
                  <p className="text-white font-semibold truncate">{addToPlaylistModal.song?.title}</p>
                  <p className="text-gray-500 text-xs truncate">{addToPlaylistModal.song?.artist}</p>
                </div>
              </div>
              <button onClick={() => setAddToPlaylistModal({ open: false, song: null })}
                      className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-white/5 flex-shrink-0">
                <X size={18} />
              </button>
            </div>

            {/* Crear playlist rápida */}
            <div className="mb-4 p-3 bg-white/5 rounded-xl border border-white/10">
              <p className="text-xs text-gray-400 mb-2">Crear nueva playlist</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={quickPlName}
                  onChange={e => setQuickPlName(e.target.value)}
                  placeholder="Nombre de la playlist"
                  className="input-glass flex-1 text-sm"
                  autoFocus
                />
                <button
                  onClick={async () => {
                    if (!quickPlName.trim()) { toast.error('Pon un nombre'); return; }
                    try {
                      const res = await axios.post(`${API}/music/playlists?user_id=${user.id}`,
                        { name: quickPlName.trim(), description: '', songs: [] });
                      const newPl = res.data;
                      // Agregar la canción a la playlist recién creada
                      await addToPlaylist(newPl.id, addToPlaylistModal.song);
                      setQuickPlName('');
                    } catch (e) {
                      toast.error('Error al crear playlist');
                    }
                  }}
                  className="btn-primary text-sm px-3 flex items-center gap-1"
                >
                  <Plus size={14} /> Crear
                </button>
              </div>
            </div>

            {/* Lista de playlists existentes */}
            <div className="max-h-72 overflow-y-auto custom-scrollbar">
              {playlists.length === 0 ? (
                <p className="text-center text-gray-500 text-sm py-6">No tienes playlists. Crea una arriba ⬆️</p>
              ) : (
                <div className="space-y-1">
                  <p className="text-xs text-gray-400 mb-2 px-1">Tus playlists ({playlists.length})</p>
                  {playlists.map(pl => (
                    <button
                      key={pl.id}
                      onClick={() => addToPlaylist(pl.id, addToPlaylistModal.song)}
                      className="w-full p-3 text-left rounded-xl bg-white/5 hover:bg-white/10 transition-colors flex items-center gap-3 border border-transparent hover:border-blue-500/40"
                    >
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                        <ListMusic size={16} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{pl.name}</p>
                        <p className="text-gray-500 text-xs">{(pl.songs || []).length} canciones</p>
                      </div>
                      <Plus size={16} className="text-blue-400 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <AIAgentModule sectionContext="música, playlists y canciones" compact={true} placeholder="Recomiéndame música o identifica una canción..." />
    </div>
  
      </>
    );
};

export default MusicModule;
